import React, { useEffect, useState } from "react";
import { AlertTriangle, Trash2, X } from "lucide-react";
import type { PlanningBlock, PlanningBlockStatus, PlanningBlockType } from "../models/production";
import { cn } from "../utils";

type ArtistOption = {
  id: string;
  name: string;
  department?: string;
};

type ProjectOption = {
  id: string;
  name: string;
};

type PlanningBlockFormState = {
  artistId: string;
  projectId: string;
  blockType: PlanningBlockType;
  startDate: string;
  endDate: string;
  allocationPercent: string;
  status: PlanningBlockStatus;
  note: string;
  department: string;
};

interface PlanningBlockModalProps {
  block: PlanningBlock | null;
  isOpen: boolean;
  artists: ArtistOption[];
  projects: ProjectOption[];
  defaultStartDate: string;
  defaultEndDate: string;
  draftDefaults?: Partial<PlanningBlock> | null;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>, block?: PlanningBlock | null) => Promise<void>;
  onDelete?: (block: PlanningBlock) => Promise<void>;
  canDelete?: boolean;
}

const BLOCK_TYPES: Array<{ value: PlanningBlockType; label: string }> = [
  { value: "project", label: "Project" },
  { value: "leave", label: "Leave" },
  { value: "internal", label: "Internal" },
  { value: "hold", label: "Hold" },
];

const STATUSES: Array<{ value: PlanningBlockStatus; label: string }> = [
  { value: "draft", label: "Draft" },
  { value: "confirmed", label: "Confirmed" },
  { value: "published", label: "Published" },
  { value: "cancelled", label: "Cancelled" },
];

function buildInitialState(block: PlanningBlock | null, defaultStartDate: string, defaultEndDate: string, draftDefaults?: Partial<PlanningBlock> | null): PlanningBlockFormState {
  const source = block || draftDefaults || null;
  return {
    artistId: source?.artistId || "",
    projectId: source?.projectId || "",
    blockType: source?.blockType || "project",
    startDate: source?.startDate || defaultStartDate,
    endDate: source?.endDate || defaultEndDate,
    allocationPercent: String(source?.allocationPercent ?? 100),
    status: source?.status || "draft",
    note: source?.note || "",
    department: source?.department || "",
  };
}

export function PlanningBlockModal({
  block,
  isOpen,
  artists,
  projects,
  defaultStartDate,
  defaultEndDate,
  draftDefaults = null,
  onClose,
  onSave,
  onDelete,
  canDelete = false,
}: PlanningBlockModalProps) {
  const [form, setForm] = useState<PlanningBlockFormState>(() => buildInitialState(block, defaultStartDate, defaultEndDate, draftDefaults));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialState(block, defaultStartDate, defaultEndDate, draftDefaults));
      setError(null);
    }
  }, [block, defaultEndDate, defaultStartDate, draftDefaults, isOpen]);

  if (!isOpen) return null;

  const selectedArtist = artists.find((artist) => artist.id === form.artistId);
  const selectedProject = projects.find((project) => project.id === form.projectId);
  const projectRequired = form.blockType === "project";

  const updateField = <K extends keyof PlanningBlockFormState>(key: K, value: PlanningBlockFormState[K]) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "artistId" ? { department: artists.find((artist) => artist.id === value)?.department || prev.department } : {}),
      ...(key === "blockType" && value !== "project" ? { projectId: "" } : {}),
    }));
  };

  const handleSave = async () => {
    setError(null);
    if (!form.artistId) {
      setError("Choose an artist.");
      return;
    }
    if (projectRequired && !form.projectId) {
      setError("Choose a project for project blocks.");
      return;
    }
    if (!form.startDate || !form.endDate) {
      setError("Choose a start and end date.");
      return;
    }
    const allocationPercent = Number(form.allocationPercent);
    if (!Number.isFinite(allocationPercent) || allocationPercent < 0 || allocationPercent > 100) {
      setError("Allocation must be between 0 and 100.");
      return;
    }

    setSaving(true);
    try {
      await onSave(
        {
          artistId: form.artistId,
          artistName: selectedArtist?.name,
          projectId: projectRequired ? form.projectId : draftDefaults?.projectId,
          projectName: projectRequired ? selectedProject?.name : draftDefaults?.projectName,
          department: form.department || selectedArtist?.department,
          blockType: form.blockType,
          startDate: form.startDate,
          endDate: form.endDate,
          allocationPercent,
          status: form.status,
          note: form.note,
          ...(block ? {} : {
            source: draftDefaults?.source,
            sourceBookingId: draftDefaults?.sourceBookingId,
            sourceFptBookingId: draftDefaults?.sourceFptBookingId,
          }),
        },
        block
      );
      onClose();
    } catch (saveError: any) {
      setError(saveError?.message || "Failed to save planning block.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!block || !onDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(block);
      onClose();
    } catch (deleteError: any) {
      setError(deleteError?.message || "Failed to delete planning block.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-semibold text-slate-950 dark:text-slate-100">{block ? "Edit planning block" : "New planning block"}</h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Internal schedule only. Flow Production Tracking publishing is not active.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Artist</span>
            <select value={form.artistId} onChange={(event) => updateField("artistId", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
              <option value="">Choose artist</option>
              {artists.map((artist) => (
                <option key={artist.id} value={artist.id}>{artist.name}</option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Block type</span>
            <select value={form.blockType} onChange={(event) => updateField("blockType", event.target.value as PlanningBlockType)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
              {BLOCK_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>

          <label className={cn("space-y-1.5", !projectRequired && "opacity-60")}>
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Project</span>
            <select disabled={!projectRequired} value={form.projectId} onChange={(event) => updateField("projectId", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:disabled:bg-slate-900/50">
              <option value="">Choose project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Department</span>
            <input value={form.department} onChange={(event) => updateField("department", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Start date</span>
            <input type="date" value={form.startDate} onChange={(event) => updateField("startDate", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">End date</span>
            <input type="date" value={form.endDate} onChange={(event) => updateField("endDate", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Allocation %</span>
            <input type="number" min="0" max="100" value={form.allocationPercent} onChange={(event) => updateField("allocationPercent", event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>

          <label className="space-y-1.5">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Status</span>
            <select value={form.status} onChange={(event) => updateField("status", event.target.value as PlanningBlockStatus)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
              {STATUSES.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </label>

          <label className="space-y-1.5 sm:col-span-2">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Note</span>
            <textarea value={form.note} onChange={(event) => updateField("note", event.target.value)} rows={3} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          </label>
        </div>

        {error ? (
          <div className="mx-5 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-200">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        ) : null}

        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/60">
          <div>
            {block && canDelete ? (
              <button onClick={handleDelete} disabled={deleting || saving} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 dark:border-red-900/60 dark:bg-slate-950 dark:hover:bg-red-950/20">
                <Trash2 className="h-4 w-4" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900">Cancel</button>
            <button onClick={handleSave} disabled={saving || deleting} className="rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-slate-100 dark:text-slate-950">
              {saving ? "Saving..." : "Save block"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
